import React from 'react';
import { QboAccount, TransactionFilterParams, StripeLinkedAccount } from '../../../api/types';
import { Search, FilterX, Building2, ShieldCheck } from 'lucide-react';
import SearchableBusinessSelect from '../../../components/SearchableBusinessSelect';
import FilterSelect from '../components/FilterSelect';

interface LedgerFiltersProps {
    filters: TransactionFilterParams;
    onFilterChange: (filters: Partial<TransactionFilterParams>) => void;
    onClearFilters: () => void;
    linkedAccounts: StripeLinkedAccount[];
    qboAccounts: QboAccount[];
    onOpenVendorManagement: () => void;
    onOpenVendorList: () => void;
    hideSearch?: boolean;
}

const LedgerFilters: React.FC<LedgerFiltersProps> = ({ filters, onFilterChange, onClearFilters, linkedAccounts, qboAccounts, onOpenVendorManagement, onOpenVendorList, hideSearch = false }) => {
    const scopedQboAccounts = qboAccounts.filter((account) => !!account.data.account_id);
    const qboAccountOptions = scopedQboAccounts.length === 0
        ? [{ id: '', name: 'QBO Account: All', subtitle: undefined }]
        : [
            {
                id: '',
                name: 'QBO Account: All',
                subtitle: undefined,
            },
            ...scopedQboAccounts.map((account) => {
                const accountId = account.data.account_id || '';
                return {
                    id: accountId,
                    name: account.data.fully_qualified_name || account.data.name || accountId,
                    subtitle: account.data.classification || account.data.account_type || undefined,
                };
            }),
        ];

    return (
        <div className="py-2 space-y-4">
            {/* Top Row: Search and Primary Filters */}
            <div className="flex flex-wrap items-center gap-3">
                {!hideSearch && (
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search vendor, description, amount..."
                            className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            value={filters.search || ''}
                            onChange={(e) => onFilterChange({ search: e.target.value })}
                        />
                    </div>
                )}

                <FilterSelect
                    value={filters.fiscal_bucket?.[0] || ''}
                    onChange={(e) => onFilterChange({ fiscal_bucket: e.target.value ? [e.target.value] : undefined })}
                >
                    <option value="">Category: All</option>
                    <option value="Labor">Labor</option>
                    <option value="COGS">Cost of Goods</option>
                    <option value="Fixed Expense">Fixed Expense</option>
                    <option value="Revenue">Revenue</option>
                    <option value="Other Expense">Other Expense</option>
                    <option value="BS">Balance Sheet</option>
                    <option value="INTERNAL TRANSFER">Internal Transfer</option>
                </FilterSelect>

                <FilterSelect
                    className="max-w-[200px] truncate"
                    value={filters.financial_account_id || ''}
                    onChange={(e) => onFilterChange({ financial_account_id: e.target.value || undefined })}
                >
                    <option value="">Account: All</option>
                    {linkedAccounts
                        .filter(account => account.status === 'active')
                        .map(account => (
                            <option key={account.financial_account_id} value={account.financial_account_id}>
                                {account.display_name || account.institution_name} (...{account.last4})
                            </option>
                        ))}
                </FilterSelect>

                <SearchableBusinessSelect
                    options={qboAccountOptions}
                    value={filters.qbo_account_id || ''}
                    onChange={(id) => onFilterChange({ qbo_account_id: id || undefined })}
                    placeholder="QBO Account: All"
                    emptyLabel="No QBO accounts found"
                    searchPlaceholder="Search QBO accounts..."
                    widthClassName="w-[260px]"
                    buttonClassName="relative w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white flex items-center text-left"
                    menuClassName="absolute top-11 right-0 z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                    optionClassName="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                    showSubtitleWhenClosed={false}
                />

                <FilterSelect
                    value={filters.direction || ''}
                    onChange={(e) => onFilterChange({ direction: e.target.value as 'inflow' | 'outflow' | undefined })}
                >
                    <option value="">Direction: All</option>
                    <option value="inflow">Inflow</option>
                    <option value="outflow">Outflow</option>
                </FilterSelect>

                <FilterSelect
                    value={filters.resolution_status === undefined ? 'all' : filters.resolution_status}
                    onChange={(e) => {
                        const val = e.target.value;
                        onFilterChange({ resolution_status: val === 'all' ? undefined : val });
                    }}
                >
                    <option value="all">Status: All</option>
                    <option value="needs_review">Needs Review</option>
                    <option value="verified">Verified</option>
                    <option value="awaiting_approval">Awaiting Approval</option>
                    <option value="processing">Processing</option>
                </FilterSelect>

                <FilterSelect
                    value={filters.operating_flag === undefined ? 'all' : filters.operating_flag ? 'true' : 'false'}
                    onChange={(e) => {
                        const val = e.target.value;
                        onFilterChange({ operating_flag: val === 'all' ? undefined : val === 'true' });
                    }}
                >
                    <option value="all">Operating: All</option>
                    <option value="true">Operating</option>
                    <option value="false">Non-Operating</option>
                </FilterSelect>
            </div>

            {/* Bottom Row: Date Range and Toggles */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    {/* <button className="flex items-center gap-1 text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-md hover:bg-gray-200">
                        <SlidersHorizontal className="h-4 w-4" />
                        More filters
                    </button> */}
                    <button
                        onClick={onClearFilters}
                        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                    >
                        <FilterX className="h-4 w-4" />
                        Clear Filter
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={onOpenVendorManagement}
                        className="flex items-center gap-2 text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-2.5 rounded-md hover:bg-indigo-100 transition-colors"
                    >
                        <ShieldCheck className="h-4 w-4" />
                        Manage Vendors
                    </button>
                    <button
                        onClick={onOpenVendorList}
                        className="flex items-center gap-2 text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-2.5 rounded-md hover:bg-indigo-100 transition-colors"
                    >
                        <Building2 className="h-4 w-4" />
                        Vendor List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LedgerFilters;
